/**
 * Keep a partner registration slug across in-tab hops (Register → Sign In →
 * Create Wallet). sessionStorage is tab-scoped so a later visit in a new tab
 * does not inherit a stale partner. Invalid / underscored slugs are rejected
 * — they never match a live white_label_slug and must not be persisted.
 */

export const PARTNER_SLUG_STORAGE_KEY = 'mm_partner_register_slug';
export const PARTNER_SLUG_PATTERN = /^[a-z0-9-]{1,50}$/;

export type PartnerSlugStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export function normalizePartnerSlug(raw: unknown): string | undefined {
  const slug = String(raw ?? '').trim().toLowerCase();
  if (!slug || !PARTNER_SLUG_PATTERN.test(slug)) return undefined;
  return slug;
}

function resolveStore(override?: PartnerSlugStorage | null): PartnerSlugStorage | null {
  if (override !== undefined) return override;
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

export function persistPartnerSlug(
  raw: unknown,
  storage?: PartnerSlugStorage | null,
): string | undefined {
  const slug = normalizePartnerSlug(raw);
  const store = resolveStore(storage);
  if (slug && store) {
    try {
      store.setItem(PARTNER_SLUG_STORAGE_KEY, slug);
    } catch {
      // Private mode / quota — attribution still uses the URL slug on this page.
    }
  }
  return slug;
}

export function readPersistedPartnerSlug(
  storage?: PartnerSlugStorage | null,
): string | undefined {
  const store = resolveStore(storage);
  if (!store) return undefined;
  try {
    return normalizePartnerSlug(store.getItem(PARTNER_SLUG_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

export function clearPersistedPartnerSlug(storage?: PartnerSlugStorage | null): void {
  const store = resolveStore(storage);
  if (!store) return;
  try {
    store.removeItem(PARTNER_SLUG_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function registerPathForSlug(raw?: unknown): string {
  const slug = normalizePartnerSlug(raw);
  return slug ? `/register/${encodeURIComponent(slug)}` : '/register';
}

export function loginPathForSlug(raw?: unknown): string {
  const slug = normalizePartnerSlug(raw);
  return slug ? `/login?partner=${encodeURIComponent(slug)}` : '/login';
}
